import { MentionType } from '../models/mention-type.enum';
import { NotificationType } from '../models/notification-type.enum';

/**
 * Information about a mention in content.
 * Matches backend MentionInfoDto.
 */
export interface MentionInfoDto {
  mentionType: MentionType;
  entityId: string;
  displayName: string;
  pattern: string;
  /**
   * Whether the current viewer may open the mentioned entity. Computed per viewer by the
   * activity feed (visibility-blocked entities render as plain text, mirroring how the
   * assignments card treats canViewResource); other producers (notes, notifications)
   * always send true. Only an explicit false suppresses the link.
   */
  canView?: boolean;
}

/**
 * Resolve the route a mention link should navigate to based on its type.
 * Centralizes routing so adding a new mention type only requires a new case here.
 */
function getMentionHref(mention: MentionInfoDto): string {
  switch (Number(mention.mentionType)) {
    case MentionType.Resource:
      return `/resources/${mention.entityId}`;
    case MentionType.Matter:
      return `/matters/${mention.entityId}`;
    case MentionType.TimeOffRequestStatus:
      return '/user-profile';
    case MentionType.Request:
      return `/requests/${mention.entityId}`;
    default:
      return '#';
  }
}

/**
 * Replace mention patterns in content with their resolved representation.
 * Each mention becomes a clickable `<a>` tag whose href is determined by its type
 * (see {@link getMentionHref}) — unless the mention carries an explicit
 * `canView: false`, in which case it degrades to its plain-text display name so the
 * viewer is never handed a link to a page their visibility settings would 404.
 * Uses the literal `pattern` field from each MentionInfoDto for direct string
 * replacement — no regex needed; the backend owns the mention format.
 */
export function resolveMentionLinks(
  content: string,
  mentions?: MentionInfoDto[],
): string {
  if (!content || !mentions?.length) return content || '';

  let result = content;
  for (const mention of mentions) {
    const replacement =
      mention.canView === false
        ? mention.displayName
        : `<a class="mention-link" href="${getMentionHref(mention)}">${mention.displayName}</a>`;
    result = result.replaceAll(mention.pattern, replacement);
  }
  return result;
}

/**
 * Replace mention patterns in content with plain-text display names.
 * Useful for contexts where HTML rendering is not desired.
 */
export function resolveMentionText(
  content: string,
  mentions?: MentionInfoDto[],
): string {
  if (!content || !mentions?.length) return content || '';

  let result = content;
  for (const mention of mentions) {
    result = result.replaceAll(mention.pattern, mention.displayName);
  }
  return result;
}

/**
 * Resolve the displayed HTML for a notification message.
 *
 * All notification types share the same mention-resolution path: tokens such as
 * `@{EmployeeId}`, `#{ClientMatterNumber}`, `¤{StatusKeyword}`, and `?{RequestId}`
 * are replaced using the metadata supplied by the backend in `mentions`.
 */
export function resolveNotificationContent(
  message: string,
  _notificationType: NotificationType,
  mentions?: MentionInfoDto[],
): string {
  if (!message) return '';
  return resolveMentionLinks(message, mentions);
}
